import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeftIcon as ArrowLeft,
  CircleNotchIcon as Spinner,
  FileCodeIcon as FileCode,
  UploadSimpleIcon as Upload,
  TrashIcon as Trash,
  EyeIcon as Eye,
  CodeIcon as Code,
  CheckCircleIcon as CheckCircle,
  WarningOctagonIcon as WarningOctagon,
  XIcon as X,
} from "@phosphor-icons/react";
import {
  listParsedPrograms,
  parseBulkZip,
  parseSingleSource,
  getParsedProgram,
  deleteParsedProgram,
} from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/auth";
import TypeBadge from "@/components/fielddeps/TypeBadge";

const LANG_BADGE = {
  RPG: { color: "#0f62fe", label: "RPG" },
  CL:  { color: "#6929c4", label: "CL" },
  DDS: { color: "#0e6027", label: "DDS" },
};

function ParsedProgramRow({ row, onView, onDelete }) {
  const badge = LANG_BADGE[row.source_type] || { color: "#525252", label: row.source_type };
  return (
    <tr
      data-testid={`parsed-row-${row.program_name}`}
      className="border-b border-[var(--ml-border)] hover:bg-[var(--ml-surface)]/50 transition-colors"
    >
      <td className="px-4 py-3 ml-mono font-semibold">{row.program_name}</td>
      <td className="px-4 py-3">
        <span
          className="inline-flex items-center px-2 py-0.5 ml-mono text-[10px] uppercase tracking-widest border font-semibold"
          style={{ borderColor: badge.color, color: badge.color }}
          data-testid={`parsed-lang-${row.program_name}`}
        >
          {badge.label}
        </span>
      </td>
      <td className="px-4 py-3 text-center" data-testid={`parsed-decl-${row.program_name}`}>{row.file_decl_count}</td>
      <td className="px-4 py-3 text-center" data-testid={`parsed-ops-${row.program_name}`}>{row.file_op_count}</td>
      <td className="px-4 py-3 text-center" data-testid={`parsed-calls-${row.program_name}`}>{row.call_count}</td>
      <td className="px-4 py-3 text-center">
        {row.error_count > 0 ? (
          <span className="text-[#a2191f] inline-flex items-center gap-1" data-testid={`parsed-err-${row.program_name}`}>
            <WarningOctagon size={13} weight="bold" /> {row.error_count}
          </span>
        ) : (
          <span className="text-[#0e6027] inline-flex items-center" data-testid={`parsed-ok-${row.program_name}`}>
            <CheckCircle size={13} weight="bold" />
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        <button
          onClick={() => onView(row)}
          data-testid={`parsed-view-${row.program_name}`}
          className="ml-btn-ghost text-xs gap-1 mr-1"
          title="View parsed facts"
        >
          <Eye size={12} weight="bold" /> View
        </button>
        <button
          onClick={() => onDelete(row)}
          data-testid={`parsed-delete-${row.program_name}`}
          className="ml-btn-ghost text-xs text-[#a2191f]"
          title="Delete parsed source"
        >
          <Trash size={12} weight="bold" />
        </button>
      </td>
    </tr>
  );
}

function FactsDrawer({ workspaceId, programName, onClose }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("facts");
  const [error, setError] = useState(null);

  useEffect(() => {
    getParsedProgram(workspaceId, programName)
      .then(setData)
      .catch((e) => setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message));
  }, [workspaceId, programName]);

  if (error) {
    return (
      <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6">
        <div className="ml-card max-w-md p-6 space-y-3" data-testid="facts-drawer-error">
          <h3 className="font-semibold text-[#a2191f]">Failed to load</h3>
          <p className="text-sm">{error}</p>
          <button onClick={onClose} className="ml-btn-primary">Close</button>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center text-white" data-testid="facts-drawer-loading">
        <Spinner size={24} className="animate-spin" />
      </div>
    );
  }

  const facts = data.facts;
  const ast = facts.ast || {};
  const isDds = data.source_type === "DDS";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end md:items-center justify-end md:justify-center" data-testid="facts-drawer">
      <div className="bg-white w-full md:max-w-3xl h-[88vh] md:h-[80vh] flex flex-col border border-[var(--ml-border)] shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--ml-border)]">
          <div>
            <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
              {data.source_type} · parsed source
            </div>
            <h3 className="text-lg font-semibold tracking-tight" data-testid="facts-drawer-name">{data.program_name}</h3>
          </div>
          <button onClick={onClose} data-testid="facts-drawer-close" className="ml-btn-ghost">
            <X size={16} weight="bold" />
          </button>
        </div>
        <div className="flex border-b border-[var(--ml-border)]">
          <button
            onClick={() => setTab("facts")}
            data-testid="facts-drawer-tab-facts"
            className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === "facts" ? "border-[var(--ml-primary)] text-[var(--ml-primary)]" : "border-transparent text-[var(--ml-text-secondary)]"}`}
          >
            Facts
          </button>
          <button
            onClick={() => setTab("source")}
            data-testid="facts-drawer-tab-source"
            className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === "source" ? "border-[var(--ml-primary)] text-[var(--ml-primary)]" : "border-transparent text-[var(--ml-text-secondary)]"}`}
          >
            Source
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {tab === "facts" ? (
            <div className="p-5 space-y-5 text-sm" data-testid="facts-drawer-facts">
              {isDds ? (
                <DdsFactsView ast={ast} />
              ) : (
                <ProgramFactsView ast={ast} facts={facts} />
              )}
              {facts.errors && facts.errors.length > 0 && (
                <section data-testid="facts-drawer-errors">
                  <h4 className="ml-mono text-[10px] uppercase tracking-widest text-[#a2191f] mb-1">Parser errors</h4>
                  <ul className="text-xs text-[#a2191f] list-disc pl-4">
                    {facts.errors.map((e) => (
                      <li key={`${e.loc?.line ?? 0}-${e.message}`}>L{e.loc?.line}: {e.message}</li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          ) : (
            <pre
              className="p-5 ml-mono text-[12px] leading-snug whitespace-pre overflow-x-auto bg-[var(--ml-surface)]/40"
              data-testid="facts-drawer-source"
            >{data.raw_source}</pre>
          )}
        </div>
      </div>
    </div>
  );
}

function ProgramFactsView({ ast, facts }) {
  return (
    <>
      <section>
        <h4 className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1.5">Stats</h4>
        <div className="grid grid-cols-5 gap-2 text-center">
          {["file_decl_count", "file_op_count", "call_count", "field_io_count", "error_count"].map((k) => (
            <div key={k} className="ml-card p-2">
              <div className="text-lg font-semibold">{facts.stats[k] ?? 0}</div>
              <div className="ml-mono text-[9px] uppercase tracking-widest text-[var(--ml-text-secondary)]">{k.replace(/_count$/, "")}</div>
            </div>
          ))}
        </div>
      </section>
      {(facts.file_refs || []).length > 0 && (
        <section>
          <h4 className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1.5">File declarations</h4>
          <ul className="space-y-1">
            {facts.file_refs.map((f) => (
              <li key={`${f.name}-${f.usage}-${f.loc.line}`} className="flex items-center gap-2 ml-mono text-xs">
                <span className="font-semibold">{f.name}</span>
                <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-widest border border-[var(--ml-border)]">{f.usage}</span>
                {f.keyed && <span className="text-[var(--ml-text-secondary)]">keyed</span>}
                {f.device && <span className="text-[var(--ml-text-secondary)]">· {f.device}</span>}
                <span className="text-[var(--ml-text-secondary)] ml-auto">L{f.loc.line}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {(facts.file_io || []).length > 0 && (
        <section>
          <h4 className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1.5">File operations</h4>
          <ul className="space-y-1">
            {facts.file_io.map((o) => (
              <li key={`${o.op}-${o.file}-${o.loc.line}`} className="flex items-center gap-2 ml-mono text-xs">
                <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-widest bg-[var(--ml-primary)]/10 text-[var(--ml-primary)]">{o.op}</span>
                <span className="font-semibold">{o.file}</span>
                <span className="text-[var(--ml-text-secondary)] ml-auto">L{o.loc.line}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {(facts.calls || []).length > 0 && (
        <section>
          <h4 className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1.5">Program calls</h4>
          <ul className="space-y-1">
            {facts.calls.map((c) => (
              <li key={`${c.call_kind}-${c.target}-${c.loc.line}`} className="flex items-center gap-2 ml-mono text-xs">
                <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-widest border border-[var(--ml-border)]">{c.call_kind}</span>
                <span className="font-semibold">{c.target}</span>
                {c.parameters?.length > 0 && (
                  <span className="text-[var(--ml-text-secondary)]">({c.parameters.join(", ")})</span>
                )}
                <span className="text-[var(--ml-text-secondary)] ml-auto">L{c.loc.line}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {(facts.field_io || []).length > 0 && (
        <section data-testid="facts-field-io">
          <h4 className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1.5">
            Field I/O (parser-proven · {facts.field_io.length})
          </h4>
          <ul className="space-y-1 max-h-64 overflow-y-auto">
            {facts.field_io.slice(0, 200).map((f, i) => (
              <li key={`${f.op}-${f.field}-${f.loc?.line}-${i}`} className="flex items-center gap-2 ml-mono text-xs" data-testid={`facts-field-io-${f.access}`}>
                <TypeBadge type={f.access} />
                <span className="font-semibold">{f.field}</span>
                <span className="text-[var(--ml-text-secondary)] truncate" title={f.note || ""}>{f.op}{f.file ? ` → ${f.file}` : ""}</span>
                <span className="text-[var(--ml-text-secondary)] ml-auto">L{f.loc?.line}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {ast.ctl_opts?.length > 0 && (
        <section>
          <h4 className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1.5">ctl-opts</h4>
          <div className="ml-mono text-xs text-[var(--ml-text-secondary)]">{ast.ctl_opts.join(" · ")}</div>
        </section>
      )}
    </>
  );
}

function DdsFactsView({ ast }) {
  const isDspf = ast.file_type === "DSPF";
  return (
    <>
      <section data-testid="dds-file-summary" data-file-type={ast.file_type}>
        <h4 className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1.5">{ast.file_type} file</h4>
        <div className="text-sm ml-mono">
          {ast.based_on && <div>Based on: <span className="font-semibold">{ast.based_on}</span></div>}
          {ast.joined_files?.length > 0 && (
            <div>Joins: {ast.joined_files.join(", ")}</div>
          )}
          {isDspf && (
            <div className="text-xs space-y-0.5" data-testid="dspf-summary">
              <div>Display file · {ast.records?.length || 0} record format{(ast.records?.length || 0) === 1 ? "" : "s"} · {ast.field_count ?? 0} field{(ast.field_count ?? 0) === 1 ? "" : "s"} · {ast.constant_count ?? 0} constant{(ast.constant_count ?? 0) === 1 ? "" : "s"} (never field dependencies)</div>
              <div>REF: {ast.ref_file ? <span className="font-semibold">{ast.ref_library ? `${ast.ref_library}/` : ""}{ast.ref_file}</span> : <span className="text-[var(--ml-text-secondary)]">none — database mapping only via REFFLD</span>}</div>
              {ast.keywords?.length > 0 && <div className="text-[var(--ml-text-secondary)]">File keywords: {ast.keywords.join(", ")}</div>}
            </div>
          )}
        </div>
      </section>
      {(ast.records || []).map((rec) => (
        <section key={rec.name} data-testid={`dds-record-${rec.name}`}>
          <h4 className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1.5">
            Record format: <span className="text-[var(--ml-primary)] font-semibold">{rec.name}</span>
            {rec.record_kind && <span className="text-[var(--ml-text-secondary)]"> · {rec.record_kind}</span>}
            {rec.text && <span className="text-[var(--ml-text-secondary)] normal-case"> · {rec.text}</span>}
            {isDspf && rec.constants?.length > 0 && <span className="text-[var(--ml-text-secondary)] normal-case"> · {rec.constants.length} constant{rec.constants.length === 1 ? "" : "s"} skipped</span>}
          </h4>
          {isDspf && rec.keywords?.length > 0 && <div className="text-[11px] text-[var(--ml-text-secondary)] ml-mono mb-1">{rec.keywords.join(" ")}</div>}
          <table className="w-full text-xs">
            <thead className="ml-mono text-[9px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
              <tr>
                <th className="text-left py-1">Field</th>
                <th className="text-left py-1">Type</th>
                <th className="text-right py-1">Len</th>
                {isDspf ? (
                  <>
                    <th className="text-left py-1 pl-3">Usage</th>
                    <th className="text-left py-1 pl-3">Row/Col</th>
                    <th className="text-left py-1 pl-3">REF / REFFLD</th>
                  </>
                ) : (
                  <th className="text-left py-1 pl-3">Heading</th>
                )}
              </tr>
            </thead>
            <tbody>
              {rec.fields.map((f) => (
                <tr key={f.name} className="border-t border-[var(--ml-border)]" data-testid={`dds-field-${rec.name}-${f.name}`}>
                  <td className="py-1 ml-mono">
                    {f.is_key && <span className="mr-1 text-[var(--ml-primary)] font-semibold" title="Key field">K</span>}
                    {f.name}
                  </td>
                  <td className="py-1 ml-mono">{f.data_type || "—"}{f.decimals != null ? `(${f.length || ""}.${f.decimals})` : ""}</td>
                  <td className="py-1 ml-mono text-right">{f.length ?? ""}</td>
                  {isDspf ? (
                    <>
                      <td className="py-1 pl-3 ml-mono" data-usage={f.usage ?? ""}>{f.usage || "blank → O"}</td>
                      <td className="py-1 pl-3 ml-mono text-[var(--ml-text-secondary)]">{f.row != null ? `${f.row}/${f.col}` : "hidden"}</td>
                      <td className="py-1 pl-3 ml-mono text-[var(--ml-text-secondary)]">{f.ref_file ? `${f.ref_library ? `${f.ref_library}/` : ""}${f.ref_file}.${f.ref_field}` : f.ref_field ? `*SRC ${f.ref_field}` : "—"}</td>
                    </>
                  ) : (
                    <td className="py-1 pl-3 text-[var(--ml-text-secondary)]">{f.column_heading || ""}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
    </>
  );
}

export default function WorkspaceSourceParser() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [lastBulkResult, setLastBulkResult] = useState(null);
  const [searchParams] = useSearchParams();
  const [drawerProgram, setDrawerProgram] = useState(() => (searchParams.get("program") || "").toUpperCase() || null);
  const [paste, setPaste] = useState({ program_name: "", source_type: "", content: "" });
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await listParsedPrograms(id);
      setList(data.programs || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const onZipChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadProgress(0);
    setLastBulkResult(null);
    try {
      const res = await parseBulkZip(id, file, (pe) => {
        if (pe.total) setUploadProgress(Math.round((pe.loaded / pe.total) * 100));
      });
      setLastBulkResult(res);
      toast.success(`Parsed ${res.parsed_count} of ${res.total_members} files (${res.skipped_count} skipped).`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = ""; // allow re-uploading the same file
    }
  };

  const onPasteSubmit = async (e) => {
    e.preventDefault();
    if (!paste.program_name.trim() || !paste.content.trim()) return;
    setUploading(true);
    try {
      const res = await parseSingleSource(id, {
        program_name: paste.program_name.trim().toUpperCase(),
        source_type: paste.source_type || null,
        content: paste.content,
      });
      if (res.persisted) {
        toast.success(`Parsed ${res.program_name} (${res.source_type}) — ${res.refs_emitted} refs emitted.`);
        setPaste({ program_name: "", source_type: "", content: "" });
        await load();
      } else {
        toast.error(`Parse failed: ${res.error || "unknown"}`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Parse failed");
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (row) => {
    if (!window.confirm(`Remove parsed source for ${row.program_name}? This will also remove its parsed program_references rows.`)) return;
    try {
      await deleteParsedProgram(id, row.program_name);
      toast.success(`Deleted parsed source for ${row.program_name}.`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8" data-testid="source-parser-page">
      <Link
        to={`/workspaces/${id}/metadata`}
        className="text-sm text-[var(--ml-text-secondary)] hover:text-[var(--ml-primary)] flex items-center gap-1 mb-4"
        data-testid="source-parser-back"
      >
        <ArrowLeft size={14} weight="bold" /> Back to repository
      </Link>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] flex items-center gap-1.5 mb-1">
            <FileCode size={11} weight="bold" /> Source Parser
          </div>
          <h1 className="text-3xl font-semibold tracking-tight">RPG / CL / DDS static parsing</h1>
          <p className="text-sm text-[var(--ml-text-secondary)] mt-1 max-w-3xl">
            Upload IBM i source members. We extract the file-I/O matrix, program-call graph, and DDS schema,
            and feed them into the Impact Graph as <code>source=&quot;parsed&quot;</code> references alongside any CSV-imported metadata.
          </p>
        </div>
        <Link
          to={`/workspaces/${id}/business-rules`}
          className="ml-btn-secondary text-xs whitespace-nowrap shrink-0 mt-1"
          data-testid="source-parser-business-rules-link"
          title="Open the cross-program Business Rule Discovery dashboard (Iter 36)"
        >
          Business rules →
        </Link>
      </div>

      {/* Upload area */}
      <div className="grid md:grid-cols-2 gap-5 mb-6">
        <div className="ml-card p-5" data-testid="source-parser-bulk-card">
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-2 flex items-center gap-1.5">
            <Upload size={11} weight="bold" /> Bulk ZIP upload
          </div>
          <p className="text-sm text-[var(--ml-text-secondary)] mb-3">
            Drop a zip containing <code>.RPGLE / .CLLE / .PF / .LF / .DSPF</code> source members. Max 32 MiB, 500 files.
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".zip"
            onChange={onZipChange}
            disabled={uploading}
            data-testid="source-parser-zip-input"
            className="block w-full text-sm file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-[var(--ml-primary)] file:text-white file:font-semibold file:cursor-pointer disabled:opacity-50"
          />
          {uploading && uploadProgress > 0 && uploadProgress < 100 && (
            <div className="text-xs text-[var(--ml-text-secondary)] mt-2" data-testid="source-parser-progress">
              Uploading… {uploadProgress}%
            </div>
          )}
        </div>

        <form className="ml-card p-5" onSubmit={onPasteSubmit} data-testid="source-parser-paste-card">
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-2 flex items-center gap-1.5">
            <Code size={11} weight="bold" /> Paste single source
          </div>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <input
              type="text"
              placeholder="Program name (e.g. QCUSTCDT)"
              value={paste.program_name}
              onChange={(e) => setPaste((p) => ({ ...p, program_name: e.target.value }))}
              required
              data-testid="source-parser-paste-name"
              className="px-3 py-2 border border-[var(--ml-border)] text-sm focus:outline-none focus:border-[var(--ml-primary)]"
            />
            <select
              value={paste.source_type}
              onChange={(e) => setPaste((p) => ({ ...p, source_type: e.target.value }))}
              data-testid="source-parser-paste-type"
              className="px-3 py-2 border border-[var(--ml-border)] text-sm focus:outline-none focus:border-[var(--ml-primary)] bg-white"
            >
              <option value="">Auto-detect</option>
              <option value="RPG">RPG</option>
              <option value="CL">CL</option>
              <option value="DDS">DDS (PF / LF, auto-detects DSPF)</option>
              <option value="DSPF">DDS display file (DSPF)</option>
            </select>
          </div>
          <textarea
            value={paste.content}
            onChange={(e) => setPaste((p) => ({ ...p, content: e.target.value }))}
            placeholder="Paste RPG / CL / DDS source here…"
            rows={5}
            required
            data-testid="source-parser-paste-content"
            className="w-full px-3 py-2 border border-[var(--ml-border)] text-xs ml-mono focus:outline-none focus:border-[var(--ml-primary)] resize-y"
          />
          <button
            type="submit"
            disabled={uploading || !paste.program_name.trim() || !paste.content.trim()}
            data-testid="source-parser-paste-submit"
            className="mt-2 ml-btn-primary text-sm w-full justify-center"
          >
            {uploading ? <Spinner size={12} className="animate-spin" /> : <Upload size={12} weight="bold" />}
            {uploading ? "Parsing…" : "Parse"}
          </button>
        </form>
      </div>

      {lastBulkResult && (
        <div className="ml-card p-5 mb-6" data-testid="source-parser-bulk-result">
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-2">
            Last bulk parse · {lastBulkResult.parsed_count} parsed · {lastBulkResult.skipped_count} skipped · {lastBulkResult.total_members} total
          </div>
          <ul className="space-y-1 max-h-32 overflow-y-auto text-xs">
            {lastBulkResult.per_file.map((f) => (
              <li
                key={f.file}
                className={`flex items-center gap-2 ml-mono ${
                  f.status === "parsed" ? "text-[#0e6027]" : f.status === "skipped" ? "text-[var(--ml-text-secondary)]" : "text-[#a2191f]"
                }`}
                data-testid={`bulk-result-${f.file}`}
              >
                {f.status === "parsed" ? <CheckCircle size={12} weight="bold" /> : <WarningOctagon size={12} weight="bold" />}
                <span className="font-semibold">{f.file}</span>
                {f.status === "parsed" && (
                  <span className="text-[var(--ml-text-secondary)]">{f.source_type} · {f.refs_emitted} refs</span>
                )}
                {f.error && <span className="text-[var(--ml-text-secondary)]">— {f.error}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Parsed programs table */}
      <div className="ml-card overflow-hidden" data-testid="parsed-programs-table">
        <div className="px-5 py-3 border-b border-[var(--ml-border)] ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] flex items-center justify-between">
          <span>Parsed programs ({list.length})</span>
          <button
            onClick={() => navigate(`/workspaces/${id}/metadata/impact`)}
            className="text-[var(--ml-primary)] hover:underline ml-mono text-[10px]"
            data-testid="source-parser-impact-link"
          >
            View on Impact Graph →
          </button>
        </div>
        {loading ? (
          <div className="px-5 py-10 text-center text-[var(--ml-text-secondary)] text-sm" data-testid="parsed-list-loading">
            <Spinner size={14} className="animate-spin inline mr-2" /> Loading…
          </div>
        ) : list.length === 0 ? (
          <div className="px-5 py-10 text-center text-[var(--ml-text-secondary)] text-sm" data-testid="parsed-list-empty">
            No source files parsed yet. Upload a zip or paste a single source above to get started.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] bg-[var(--ml-surface)]/50">
              <tr>
                <th className="px-4 py-2 text-left">Program</th>
                <th className="px-4 py-2 text-left">Type</th>
                <th className="px-4 py-2 text-center">Files</th>
                <th className="px-4 py-2 text-center">I/O ops</th>
                <th className="px-4 py-2 text-center">Calls</th>
                <th className="px-4 py-2 text-center">Status</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {list.map((row) => (
                <ParsedProgramRow
                  key={row.program_name}
                  row={row}
                  onView={(r) => setDrawerProgram(r.program_name)}
                  onDelete={onDelete}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {drawerProgram && (
        <FactsDrawer
          workspaceId={id}
          programName={drawerProgram}
          onClose={() => setDrawerProgram(null)}
        />
      )}
    </div>
  );
}
